import { useEffect, useMemo, useRef, useState } from 'react'
import { standardId } from '../../../utils/standardRef'
import { useModal } from '../../../context/ModalContext'
import { IconFilter } from '../../../layout/NavRail/NavRail'
import MultiSelect from '../../inputs/MultiSelect'
import { useGuides } from '../../../hooks/useGuides'
import { useTopics } from '../../../hooks/useTopics'
import { updateGuide, deleteGuide, guideKind, guideOpenUrl } from '../../../services/guides'
import { itemTreeToOptions } from '../comments/commentUtils'
import GuidesFilterPopover from './GuidesFilterPopover'
import {
  GUIDE_SORTS,
  emptyGuideFilter,
  loadGuideFilter,
  makeGuideMatcher,
  saveGuideFilter,
  sortGuides,
} from './guideFilter'
import '../comments/Comments.css'
import './GuidesPane.css'

const KIND_LABEL = { url: 'LINK', image: 'IMG', video: 'VID', pdf: 'PDF', file: 'FILE' }

// Turn the open item into a picker/anchor option.
function itemToOption(item) {
  if (!item?._id) return null
  return {
    _id: item._id,
    label: item.label,
    standard: item.standard,
    order: item.order,
    path: item.path || [],
    pageStart: item.pageStart,
    pageEnd: item.pageEnd,
  }
}

// Build the viewer anchor a linked Item doc should open to.
function itemToAnchor(it) {
  return {
    item: it._id,
    label: it.label,
    standard: it.standard,
    order: it.order,
    path: it.path || [],
    pageStart: it.pageStart,
    pageEnd: it.pageEnd,
  }
}

function GuideRow({ guide, standard, onView, onEdit, onDelete, onRemoveFromStandard, onOpenItem }) {
  const kind = guideKind(guide)
  const title = guide.title || guide.name || 'Untitled guide'
  const standardItems = (guide.items || []).filter((it) => it && it.standard === standard)

  return (
    <article className="gds-row" id={`gds-${guide._id}`}>
      <header className="gds-row-head">
        <button type="button" className="gds-row-title" title={title} onClick={() => onView(guide)}>
          <span className="gds-row-kind">{KIND_LABEL[kind] || 'FILE'}</span>
          <span className="gds-row-name">{title}</span>
        </button>
      </header>

      {guide.description ? <p className="gds-row-desc">{guide.description}</p> : null}

      {standardItems.length ? (
        <div className="cmt-tags">
          {standardItems.map((it) => (
            <button
              key={it._id}
              type="button"
              className="cmt-tag is-open"
              title={`${it.label}${it.pageStart != null ? ` · p.${it.pageStart}` : ''} — open in viewer`}
              onClick={() => onOpenItem?.(itemToAnchor(it))}
            >
              <span className="cmt-tag-label">{it.label}</span>
              {it.pageStart != null ? <span className="cmt-tag-page">· p.{it.pageStart}</span> : null}
            </button>
          ))}
        </div>
      ) : null}

      {guide.topics?.length ? (
        <div className="gds-row-topics">
          {guide.topics.map((t) => (
            <span key={t._id} className="gds-row-topic">
              {t.name}
            </span>
          ))}
        </div>
      ) : null}

      <footer className="gds-row-actions">
        <button type="button" onClick={() => onView(guide)}>
          View
        </button>
        <a href={guideOpenUrl(guide)} target="_blank" rel="noopener noreferrer">
          Open
        </a>
        <button type="button" onClick={() => onEdit(guide)}>
          Edit
        </button>
        <button type="button" onClick={() => onRemoveFromStandard(guide)}>
          Remove here
        </button>
        <button type="button" className="is-danger" onClick={() => onDelete(guide)}>
          Delete
        </button>
      </footer>
    </article>
  )
}

// Guides view of the Code page's notes window. Lists the user's guides linked to
// an item in the open standard, with the same filter/compose rhythm as comments:
// a filter popover + sort, a scroll list, and a composer to attach existing
// guides to the current item (or create a new one pre-linked to it).
export function GuidesPane({ item, items, windowId, onOpenItem, locate, onLocated }) {
  const { openModal } = useModal()
  const { guides, status, reload } = useGuides()
  const { topics: topicOptions } = useTopics()
  const standard = item?.standard

  const [filter, setFilter] = useState(() => loadGuideFilter(windowId))
  const [filterAnchor, setFilterAnchor] = useState(null)
  const [pending, setPending] = useState([])
  const [busy, setBusy] = useState(false)

  const onLocatedRef = useRef(onLocated)
  onLocatedRef.current = onLocated
  const locateCleared = useRef(null)
  const locateSawClear = useRef(null)
  const locateRetried = useRef(null)

  useEffect(() => {
    saveGuideFilter(windowId, filter)
  }, [windowId, filter])

  const patchFilter = (partial) => setFilter((prev) => ({ ...prev, ...partial }))
  const clearFilter = () =>
    setFilter((prev) => ({ ...emptyGuideFilter(), matchMode: prev.matchMode, sort: prev.sort }))

  // Guides linked to at least one item in the open standard.
  const scoped = useMemo(
    () => guides.filter((g) => (g.items || []).some((it) => it && it.standard === standard)),
    [guides, standard]
  )

  const itemOptions = useMemo(() => itemTreeToOptions(items), [items])
  const matcher = useMemo(() => makeGuideMatcher(filter), [filter])
  const visible = useMemo(
    () => sortGuides(scoped.filter(matcher.match), filter.sort),
    [scoped, matcher, filter.sort]
  )

  useEffect(() => {
    if (!locate || locate.kind !== 'guide' || status !== 'ready') return
    if (locateCleared.current === locate.n) return
    const guide = guides.find((g) => String(g._id) === String(locate.id))
    const inStandard = guide && (guide.items || []).some((it) => it && it.standard === standard)
    if (inStandard && matcher.match(guide)) return
    locateCleared.current = locate.n
    setFilterAnchor(null)
    setFilter((prev) => ({ ...emptyGuideFilter(), matchMode: prev.matchMode, sort: prev.sort }))
  }, [locate, status, guides, matcher, standard])

  useEffect(() => {
    if (!locate || locate.kind !== 'guide') return
    const el = document.getElementById(`gds-${locate.id}`)
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      el.classList.add('is-located')
      window.setTimeout(() => el.classList.remove('is-located'), 1600)
      onLocatedRef.current?.()
      return
    }
    if (status !== 'ready') return
    if (locateCleared.current === locate.n && locateSawClear.current !== locate.n) {
      locateSawClear.current = locate.n
      return
    }
    if (locateRetried.current === locate.n) {
      onLocatedRef.current?.()
      return
    }
    locateRetried.current = locate.n
    reload()
  }, [locate, visible, status, reload])

  const toggleFilter = (event) => {
    const button = event.currentTarget
    setFilterAnchor((current) => (current ? null : button))
  }

  const linkPendingToItem = async () => {
    if (!item?._id || !pending.length || busy) return
    setBusy(true)
    try {
      await Promise.all(
        pending.map((g) => {
          const itemIds = Array.from(new Set([...(g.items || []).map((it) => it._id), item._id]))
          return updateGuide(g._id, { items: itemIds })
        })
      )
      setPending([])
      await reload()
    } finally {
      setBusy(false)
    }
  }

  const newGuide = () => {
    openModal('guide-form', { initialItem: itemToOption(item), onSaved: reload })
  }

  const viewGuide = (guide) => openModal('guide-view', { guide })
  const editGuide = (guide) => openModal('guide-form', { guide, onSaved: reload })

  const removeGuide = (guide) =>
    openModal('confirm', {
      title: 'Delete guide',
      message: `Delete "${guide.title || guide.name || 'this guide'}"? It will be removed everywhere it's attached.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => deleteGuide(guide._id),
      onConfirmed: reload,
    })

  const removeFromStandard = async (guide) => {
    const kept = (guide.items || []).filter((it) => standardId(it.standard) !== standardId(standard)).map((it) => it._id)
    await updateGuide(guide._id, { items: kept })
    await reload()
  }

  return (
    <div className="code-comments">
      <div className="cmt-toolbar">
        <button
          type="button"
          className={'cmt-filter-btn' + (filterAnchor ? ' is-active' : '')}
          onClick={toggleFilter}
          aria-haspopup="dialog"
          aria-expanded={Boolean(filterAnchor)}
        >
          <IconFilter />
          Filter
          {matcher.active ? <span className="cmt-filter-dot" aria-hidden="true" /> : null}
        </button>
        <select
          className="cmt-sort"
          value={filter.sort}
          onChange={(e) => patchFilter({ sort: e.target.value })}
          aria-label="Sort guides"
        >
          {GUIDE_SORTS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="code-comments-scroll">
        {status === 'loading' ? (
          <p className="code-comments-empty">Loading guides…</p>
        ) : status === 'error' ? (
          <p className="code-comments-empty">Could not load guides.</p>
        ) : visible.length === 0 ? (
          <p className="code-comments-empty">
            {scoped.length
              ? 'No guides match these filters.'
              : `No guides on ${standard || 'this document'} yet. Attach one below or create a new guide.`}
          </p>
        ) : (
          <div className="cmt-list">
            {visible.map((guide) => (
              <GuideRow
                key={guide._id}
                guide={guide}
                standard={standard}
                onView={viewGuide}
                onEdit={editGuide}
                onDelete={removeGuide}
                onRemoveFromStandard={removeFromStandard}
                onOpenItem={onOpenItem}
              />
            ))}
          </div>
        )}
      </div>

      <div className="code-comments-composer gds-composer">
        <div className="gds-composer-row">
          <MultiSelect
            value={pending}
            onChange={setPending}
            options={guides}
            getOptionValue={(g) => g._id}
            getOptionLabel={(g) => g.title || g.name || 'Guide'}
            placeholder="Attach existing guides…"
          />
        </div>
        <div className="gds-composer-actions">
          <button type="button" className="cmt-chip" onClick={newGuide}>
            + New guide
          </button>
          <button
            type="button"
            className="cmt-post"
            onClick={linkPendingToItem}
            disabled={busy || !pending.length || !item?._id}
            title={item?.label ? `Attach to ${item.label}` : 'Open an item first'}
          >
            {busy ? 'Adding…' : `Add to ${item?.label || 'item'}`}
          </button>
        </div>
      </div>

      {filterAnchor && (
        <GuidesFilterPopover
          anchorEl={filterAnchor}
          filter={filter}
          onPatch={patchFilter}
          onClear={clearFilter}
          onClose={() => setFilterAnchor(null)}
          active={matcher.active}
          topicOptions={topicOptions}
          itemOptions={itemOptions}
        />
      )}
    </div>
  )
}
